import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import { CommonCtx } from '../../../../context/CommonCtxProvider';
import { useQuery, useQueryClient } from 'react-query';
import { MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import { _DatatablePageEvent, _Pagination, _Venue } from '../../../../constants/staticTypes';
import { defaultPaginationEvent } from '../../../../helpers/commonHelper';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxSearch from '../../../../components/common/MlxSearch/MlxSearch';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import VenueList from './components/VenueList';
import logger from '../../../../utils/logger';
import { _VenueFormType, getVenueList } from '../../../../helpers/venueHelper';
import { ADD_VENUE, MANAGE_VENUE, VENUE_LIST } from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import AddVenueSidebar from './components/AddVenueSidebar';
import { checkActionSubPermission } from '../../../../utils/common';
import { AuthCtx } from '../../../../context/AuthCtxProvider';

const searchColumns = ['tenantName'];

const languageTexts = {
    MANAGE_VENUE: MANAGE_VENUE,
    VENUE_LIST: VENUE_LIST,
    ADD_VENUE: ADD_VENUE,
};

function VenueListManagement() {
    const { width: screenWidth } = useWindowSize();
    const { showSideBar, setShowSideBar } = useContext(CommonCtx);
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 1200);
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const { userType } = useContext(AuthCtx);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [isVenueEdit, setIsVenueEdit] = useState<boolean>(false);
    const [venueEditData, setVenueEditData] = useState<_VenueFormType>();
    const [dataTableEvent, setDataTableEvent] = useState<_DatatablePageEvent>(defaultPaginationEvent);
    const [venueListData, setVenueListData] = useState<any>(null);
    const [venueAdded, setVenueAdded] = useState<boolean>(false);

    const venueListQuery = useQuery({
        queryKey: VENUE_LIST,
        queryFn: () => getVenueList(queryClient, (dataTableEvent.page ?? 0) + 1, dataTableEvent.rows, dataTableEvent.search),
        onSuccess: (response) => {
            const paginationData = response?.data?.data?.pagination as _Pagination;
            const responseData = response?.data?.data?.lists as _Venue;
            setVenueListData(responseData);
            setDataTableEvent((event) => ({ ...event, totalRows: paginationData?.total }));
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (venueAdded) {
            venueListQuery.refetch();
            setVenueAdded(false);
        }
    }, [venueAdded]);

    const venueListQueryRef = useRef(venueListQuery);

    useEffect(() => {
        venueListQueryRef.current.refetch();
    }, [dataTableEvent?.forceUpdate]);

    useEffect(() => {
        const searchPayload = searchColumns.map((column) => `&search[${column}]=${debouncedSearchValue}`).join('');
        setDataTableEvent((event) => ({
            ...event,
            search: debouncedSearchValue.trim().length > 0 ? searchPayload : '',
            forceUpdate: event.forceUpdate + 1,
        }));
    }, [debouncedSearchValue]);

    const openEditVenueSidebar = (editableData: _VenueFormType) => {
        setVenueEditData(editableData);
        setIsVenueEdit(true);
        setShowSideBar(true);
    };

    return (
        <>
            <Suspense fallback={<div>Loading...</div>}>
                <AddVenueSidebar
                    showSideBar={showSideBar}
                    setShowSideBar={setShowSideBar}
                    isVenueEdit={isVenueEdit}
                    setIsVenueEdit={setIsVenueEdit}
                    venueEditData={venueEditData}
                    setVenueAdded={setVenueAdded}
                />
            </Suspense>

            <div className="page-section">
                <span className="page-header">{translatedText.MANAGE_VENUE}</span>
                <div className="page-content h-100">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{searchBarVisible ? translatedText.VENUE_LIST : ''}</span>
                        <div className="header-fields">
                            {(userType === 'organizationAdmin' ||
                                checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE, OrgSubPermissionKeys.ADD_VENUE)) && (
                                <MlxButton
                                    className="ms-2"
                                    removeLabelOnMobile={true}
                                    size="small"
                                    label={translatedText.ADD_VENUE}
                                    icon="ri-add-large-line"
                                    iconPos="right"
                                    onClick={() => setShowSideBar(true)}
                                />
                            )}
                            <MlxSearch
                                className="ms-2 p-inputtext-sm"
                                removeLabelOnMobile={true}
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                searchBarVisible={setSearchBarVisible}
                                isLoading={venueListQuery?.isFetching && !!debouncedSearchValue.trim().length}
                            />
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-hidden">
                        <VenueList
                            dataTableEvent={dataTableEvent}
                            setDataTableEvent={setDataTableEvent}
                            openEditVenueSidebar={openEditVenueSidebar}
                            venueListData={venueListData}
                            setVenueListData={setVenueListData}
                            venueListQuery={venueListQuery}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(VenueListManagement);
